
/*
 * VNCtalk - an enterprise real-time communication solution including chat, video and audio conferencing, screen sharing, voice messaging, file sharing, broadcasts, document collaboration and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable } from "@angular/core";
import { Observable, Subject, BehaviorSubject, debounceTime, take, distinctUntilChanged } from "rxjs";
import { CommonUtil } from "app/talk/utils/common.util";
import { Broadcaster } from "../shared/providers";
import { LoggerService } from "app/shared/services/logger.service";

@Injectable()
export class AudioOutputService {
  private onDeviceChange$ = new Subject<any>();
  private proximityState$ = new BehaviorSubject<number>(0);
  private blueToothHeadsetState$ = new BehaviorSubject<boolean>(false);

  constructor(private broadcaster: Broadcaster, private logger: LoggerService) {
    // AudioToggle.switchMicrophone = (micDeviceId: string) => {
    //   broadcaster.broadcast("switchMicrophone", micDeviceId);
    // };

    this.broadcaster.on<boolean>("onSpeakerEnabled").subscribe(isEnabled => {
      if (!CommonUtil.isOnIOS()) {
        this.logger.info("[AudioOutputService][enablelocal onSpeakerEnabled from CallKit]", isEnabled);
        if (isEnabled) {
          this._setAudioMode(AudioToggle.SPEAKER);
        } else {
          this.isBluetoothHeadsetOrHeadphoneConnected().subscribe(available => {
            if (available) {
              this._setAudioMode(AudioToggle.BLUETOOTH);
            } else {
              this._setAudioMode(AudioToggle.EARPIECE);
            }
          });
        }
      }
    });

    this.onDeviceChange$.pipe(debounceTime(200)).subscribe(() => {
      this.correctAudioOutput();
    });

    this.proximityState$.pipe(distinctUntilChanged()).subscribe(() => {
      this.correctAudioOutput();
    });
    if (CommonUtil.isOnAndroid()) {
      const validDeviceTypes = [7, 8, 18];
      AudioToggle.getOutputDevices(res => {
        for (let i = 0; i < res.devices.length; ++i) {
          const type = res.devices[i].type;
          if (validDeviceTypes.includes(type)) {
            this.logger.info("AudioTogglePlugin HeadsetDetection bluetoothstate init true -> ", JSON.stringify(res.devices[i]));
            this.blueToothHeadsetState$.next(true);
          }
        }
      });
    }

  }

  private _audioMode$ = new BehaviorSubject<string>("earpiece");
  setProximityState(state) {
    this.proximityState$.next(state);
  }

  // SPEAKER
  useSpeakerAudioOutput() {
    this.logger.info("[AudioOutputService][useSpeakerAudioOutput]");

    if (CommonUtil.isOnIOS()) {
      return;
      this._changeAudioOutputiOS(AudioToggle.SPEAKER);
    } else if (CommonUtil.isOnAndroid()) {
      this._changeAudioOutputAndroid(AudioToggle.SPEAKER);
    }
    this.broadcaster.broadcast("notifySpeakerEnabled", true);
  }

  // EARPIECE
  useDefaultAudioOutput() {
    this.logger.info("[AudioOutputService][useDefaultAudioOutput]");

    if (CommonUtil.isOnIOS()) {
      this._changeAudioOutputiOS(AudioToggle.EARPIECE);
    } else if (CommonUtil.isOnAndroid()) {
      this._changeAudioOutputAndroid(AudioToggle.EARPIECE);
      this.broadcaster.broadcast("notifySpeakerEnabled", false);
    }
  }

  // BLUETOOTH
  useBluetoothAudioOutput() {
    this.logger.info("[AudioOutputService][useBluetoothAudioOutput]");

    if (CommonUtil.isOnIOS()) {
      this._changeAudioOutputiOS(AudioToggle.BLUETOOTH);
    } else if (CommonUtil.isOnAndroid()) {
      this._changeAudioOutputAndroid(AudioToggle.BLUETOOTH);
    }
  }

  correctAudioOutput() {
    if (!CommonUtil.isOnNativeMobileDevice()) {
      return;
    }

    if (CommonUtil.isOnIOS()) {
      // disable for IOS - since cordova-ios 6.3.0 and without cordova-plugin-iosrtc output device
      // is switched automatically to headset or bluetooth when connecting and back to speaker when disconnecting
      // hence - just make sure to call it for speaker once.
      this.useSpeakerAudioOutput();
      return;
      this.logger.info("[AudioOutputService][correctAudioOutput]", this._audioMode$.value);
      // set output
      if (this._audioMode$.value === AudioToggle.SPEAKER) {
        this.useSpeakerAudioOutput();
      } else if (this._audioMode$.value === AudioToggle.BLUETOOTH) {
        this.useBluetoothAudioOutput();
      } else {
        this.useDefaultAudioOutput();
      }
    }
    if (CommonUtil.isOnAndroid()) {
      this.logger.info("[AudioOutputService][correctAudioOutput] start ", this.blueToothHeadsetState$.value, this.proximityState$.value);
      this.isBluetoothHeadsetOrHeadphoneConnected().subscribe(available => {
        if (available && this.blueToothHeadsetState$.value) {
          this.logger.info("[AudioOutputService][correctAudioOutput] => Bluetooth ", available, this.blueToothHeadsetState$.value);
          this.useBluetoothAudioOutput();
          this.broadcaster.broadcast("notifySpeakerEnabledBluetooth", false);
        } else {
          this.isWiredHeadsetOrHeadphoneConnectedAndroid().subscribe(wired => {
            if (wired) {
              this.logger.info("[AudioOutputService][correctAudioOutput] => Wired");
              this._changeAudioOutputAndroid(AudioToggle.WIRED);
              this.broadcaster.broadcast("notifySpeakerEnabled", false);
            } else {
              if (this.proximityState$.value === 1) {
                this.logger.info("[AudioOutputService][correctAudioOutput] => Earpiece ", this.proximityState$.value);
                this._changeAudioOutputAndroid(AudioToggle.EARPIECE);
                this.broadcaster.broadcast("notifySpeakerEnabled", false);
              } else {
                this.logger.info("[AudioOutputService][correctAudioOutput] => Speaker", this.proximityState$.value);
                this.useSpeakerAudioOutput();
                this.broadcaster.broadcast("notifySpeakerEnabled", true);
              }
            }
          });
        }
      });
    }
  }

  setInitialAudioOutput(mode = "speaker") {
    if (!CommonUtil.isOnNativeMobileDevice()) {
      return;
    }

    this._setAudioMode(mode);

    this.logger.info("[AudioOutputService][setInitialAudioOutput]", mode);

    this.correctAudioOutput();
  }

  private _changeAudioOutputAndroid(mode) {
    this.logger.info("[AudioOutputService][_changeAudioOutputAndroid]", mode);

    AudioToggle.setAudioMode(mode);
    this._setAudioMode(mode);
  }

  private _changeAudioOutputiOS(mode = "speaker") {
    const output = mode === "speaker" ? "speaker" : "earpiece";
    // iosDebug
    return;
    this.logger.info("[AudioOutputService][changeAudioOutputiOS]", mode, output);

    const audioRouteValue = mode === "speaker" ? "speaker" : "default";
    cordova.plugins.audioroute.overrideOutput(audioRouteValue, success => {
      this._setAudioMode(mode);
      this.logger.info("[AudioOutputService][changeAudioOutputiOS] success", success);
    }, error => {
      this.logger.error("[AudioOutputService][changeAudioOutputiOS] error", error);
    });
  }

  getAudioMode(): Observable<string> {
    return this._audioMode$;
  }

  private _setAudioMode(mode: string) {
    if (CommonUtil.isOnIOS()) {
      return;
    }
    this.logger.info("[AudioOutputService][_setAudioMode]", mode);
    this._audioMode$.next(mode);
  }

  //

  isWiredHeadsetOrHeadphoneConnected(){
    this.logger.info("[AudioOutputService][isWiredHeadsetOrHeadphoneConnected]");
    if (CommonUtil.isOnIOS()) {
      return this.isWiredHeadsetOrHeadphoneConnectediOS();
    } else if (CommonUtil.isOnAndroid()) {
      return this.isWiredHeadsetOrHeadphoneConnectedAndroid();
    }
  }

  enableDefaultOutput(): void {
    this.logger.info("[AudioOutputService][enableDefaultOutput]");
    this.isBluetoothHeadsetOrHeadphoneConnected().subscribe(available => {
      if (available) {
        this.useBluetoothAudioOutput();
      } else {
        this.useDefaultAudioOutput();
      }
    });

    // this.broadcaster.broadcast("notifySpeakerEnabled", false);

  }

  enableDefaultSpeakerOutput(): void {
    this.logger.info("[AudioOutputService][enableDefaultSpeakerOutput]");
    this.isBluetoothHeadsetOrHeadphoneConnected().subscribe(available => {
      if (available) {
        this.useBluetoothAudioOutput();
      } else {
        this.isWiredHeadsetOrHeadphoneConnected().subscribe(wiredHeadsetAvailable => {
          if (wiredHeadsetAvailable) {
            this.useDefaultAudioOutput();
          } else {
            this.useSpeakerAudioOutput();
          }
        });
      }
    });
  }

  public isBluetoothHeadsetOrHeadphoneConnected(){
    this.logger.info("[AudioOutputService][isBluetoothHeadsetOrHeadphoneConnected]");
    if (CommonUtil.isOnIOS()) {
      return this.isBluetoothHeadsetOrHeadphoneConnectediOS();
    } else if (CommonUtil.isOnAndroid()) {
      return this.isBluetoothHeadsetOrHeadphoneConnectedAndroid();
    }
  }

  //

  private isWiredHeadsetOrHeadphoneConnectedAndroid(){
    // https://developer.android.com/reference/android/media/AudioDeviceInfo
    // 4, 3, 22 -> WIRED
    const validDeviceTypes = [3, 4, 22];
    return this.isHeadsetOrHeadphoneConnectedAndroid(validDeviceTypes);
  }

  private isBluetoothHeadsetOrHeadphoneConnectedAndroid(){
    // https://developer.android.com/reference/android/media/AudioDeviceInfo
    // 7, 8 -> BLUETOOTH
    // const validDeviceTypes = [7, 8, 18]; 18 is for telephony, should not be used when no callkit support
    const validDeviceTypes = [7, 8];
    return this.isHeadsetOrHeadphoneConnectedAndroid(validDeviceTypes, true);
  }

  private isHeadsetOrHeadphoneConnectedAndroid(validDeviceTypes, bluetooth = false){
    const response = new Subject<boolean>();

    this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectedAndroid] validDeviceTypes", validDeviceTypes);
    this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectedAndroid] bluetooth", bluetooth);
    this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectedAndroid] bluetooth state", this.blueToothHeadsetState$.value);

    AudioToggle.getOutputDevices(res => {
      if (bluetooth && !this.blueToothHeadsetState$.value) {
        response.next(false);
        return;
      }
      for (let i = 0; i < res.devices.length; ++i) {
        const type = res.devices[i].type;
        this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectedAndroid] type", type, validDeviceTypes.includes(type), validDeviceTypes, res.devices);
        if (validDeviceTypes.includes(type)) {
          response.next(true);
          return;
        }
      }

      response.next(false);
    });

    return response.asObservable().pipe(take(1));
  }

  //

  private isWiredHeadsetOrHeadphoneConnectediOS(){
    this.logger.info("[AudioOutputService][isWiredHeadsetOrHeadphoneConnectediOS]");
    // https://github.com/saghul/cordova-plugin-audioroute#currentoutputssuccesscallback-errorcallback
    const validDeviceTypes = ["headphones"];
    return this.isHeadsetOrHeadphoneConnectediOS(validDeviceTypes);
  }

  private isBluetoothHeadsetOrHeadphoneConnectediOS(){
    this.logger.info("[AudioOutputService][isBluetoothHeadsetOrHeadphoneConnectediOS]");
    // https://github.com/saghul/cordova-plugin-audioroute#currentoutputssuccesscallback-errorcallback
    const validDeviceTypes = ["bluetooth-a2dp", "bluetooth-le", "bluetooth-hfp", "airplay"];
    // "bluetooth-a2dp" - Output on a Bluetooth A2DP device (Advanced Audio Distribution Profile)
    // "bluetooth-le" - Output on a Bluetooth Low Energy device
    // "bluetooth-hfp" - Hands-Free Profile (HFP)
    // "airplay" - Output on a remote Air Play device
    return this.isHeadsetOrHeadphoneConnectediOS(validDeviceTypes);
  }

  private isHeadsetOrHeadphoneConnectediOS(validDeviceTypes){
    this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectediOS]", validDeviceTypes);
    const response = new Subject<boolean>();

    cordova.plugins.audioroute.currentOutputs(outputs => {
      this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectediOS] success", outputs);
      const found = outputs.some(r => validDeviceTypes.includes(r));
      this.logger.info("[AudioOutputService][isHeadsetOrHeadphoneConnectediOS] found", found);
      response.next(found);
    }, error => {
      this.logger.error("[AudioOutputService][isHeadsetOrHeadphoneConnectediOS] error", error);
    });

    return response.asObservable().pipe(take(1));
  }

  public onMobileDeviceChanges(status) {
    if (status === "bluetoothHeadsetAdded") {
      this.blueToothHeadsetState$.next(true);
    } else if (status === "bluetoothHeadsetRemoved") {
      this.blueToothHeadsetState$.next(false);
    } else if (status === "bluetoothAdapterStarted") {
      setTimeout(() => {
        this.blueToothHeadsetState$.next(true);
        this.onDeviceChange$.next(Date.now);
      }, 3000);
    }
    this.onDeviceChange$.next(Date.now);
  }
}
